import { useMemo, useState } from "react";
import { format, parseISO } from "date-fns";
import { ptBR } from "date-fns/locale";
import {
  Clock,
  CheckCircle2,
  XCircle,
  ThumbsUp,
  ThumbsDown,
  MoreVertical,
  Loader2,
  Calendar,
  Eye,
  Copy,
  User,
  ListTodo,
  Trash2,
  FolderKanban,
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { TaskCompleteDialog } from "./TaskCompleteDialog";
import { TaskEditDateDialog } from "./TaskEditDateDialog";
import { TaskDetailSheet } from "./TaskDetailSheet";
import { TaskPriorityBadge } from "./TaskPriorityBadge";
import { TaskCancelDialog, type TaskCancelMode } from "./TaskCancelDialog";
import { LinkTaskToProjectDialog } from "./LinkTaskToProjectDialog";
import type { WorkspaceProject } from "@/hooks/useWorkspaceProjects";
import {
  getSubtaskProgress,
  type CancelTaskResult,
  type UpdateTaskInput,
  type WorkspaceTask,
} from "@/hooks/useWorkspaceTasks";
import { useToast } from "@/hooks/use-toast";
import { getTaskUrgency } from "@/lib/task-urgency";
import {
  allSubtasksCompleted,
  COMPLETE_WITH_PENDING_SUBTASKS_MESSAGE,
} from "@/lib/task-date-validation";
import { cn } from "@/lib/utils";

interface TaskListProps {
  tasks: WorkspaceTask[];
  loading: boolean;
  isAdmin: boolean;
  currentMemberId: string | null;
  workspaceId: string;
  onComplete: (taskId: string, outcome: "positive" | "negative") => Promise<void>;
  onReopen: (taskId: string) => Promise<void>;
  onCancel: (taskId: string, reason?: string) => Promise<CancelTaskResult>;
  onUpdateDueDate: (input: {
    taskId: string;
    due_date: string | null;
    due_at: string | null;
    allowPastDue?: boolean;
  }) => Promise<void>;
  onUpdateTask: (input: UpdateTaskInput) => Promise<void>;
  onDuplicate?: (task: WorkspaceTask) => void;
  projects?: WorkspaceProject[];
}

export function TaskList({
  tasks,
  loading,
  isAdmin,
  currentMemberId,
  workspaceId,
  onComplete,
  onReopen,
  onCancel,
  onUpdateDueDate,
  onUpdateTask,
  onDuplicate,
  projects,
}: TaskListProps) {
  const [completeTarget, setCompleteTarget] = useState<WorkspaceTask | null>(null);
  const [editDateTarget, setEditDateTarget] = useState<WorkspaceTask | null>(null);
  const [detailTarget, setDetailTarget] = useState<WorkspaceTask | null>(null);
  const [linkTarget, setLinkTarget] = useState<WorkspaceTask | null>(null);
  const [cancelTarget, setCancelTarget] = useState<WorkspaceTask | null>(null);
  const { toast } = useToast();

  const detailTask = useMemo(() => {
    if (!detailTarget) return null;
    return tasks.find((t) => t.id === detailTarget.id) ?? detailTarget;
  }, [detailTarget, tasks]);

  const cancelModeFor = (task: WorkspaceTask): TaskCancelMode => {
    if (isAdmin) return "cancel";
    if (task.created_by_member_id === currentMemberId) return "cancel";
    return "request";
  };

  /**
   * Abre o fluxo de conclusão apenas quando todas as subtarefas estão concluídas.
   * Caso contrário, avisa o usuário e bloqueia a ação.
   */
  const requestComplete = (task: WorkspaceTask) => {
    if (!allSubtasksCompleted(getSubtaskProgress(task))) {
      toast({
        title: "Subtarefas pendentes",
        description: COMPLETE_WITH_PENDING_SUBTASKS_MESSAGE,
        variant: "destructive",
      });
      return;
    }
    setCompleteTarget(task);
  };

  const handleConfirmCancel = async (reason?: string) => {
    if (!cancelTarget) return;
    try {
      const result = await onCancel(cancelTarget.id, reason);
      if (result === "requested") {
        toast({
          title: "Solicitação enviada",
          description: "O gestor irá avaliar a exclusão desta tarefa.",
        });
      } else if (result === "already_requested") {
        toast({
          title: "Solicitação já existe",
          description: "Já há uma solicitação de exclusão pendente para esta tarefa.",
        });
      } else {
        toast({ title: "Tarefa cancelada" });
      }
    } catch (err: unknown) {
      toast({
        title: "Erro ao cancelar tarefa",
        description: err instanceof Error ? err.message : String(err),
        variant: "destructive",
      });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (tasks.length === 0) {
    return (
      <Card className="p-10 text-center text-muted-foreground">Nenhuma tarefa por aqui.</Card>
    );
  }

  return (
    <>
      <div className="space-y-2">
        {tasks.map((task) => {
          const isCompleted = task.status === "completed";
          const isCancelled = task.status === "cancelled";
          const urgency = getTaskUrgency({
            status: task.status,
            due_date: task.due_date,
            due_at: task.due_at,
          });
          const canEdit = isAdmin || task.assigned_member_id === currentMemberId;
          const taskTags = task.tags ?? [];
          const subProgress = getSubtaskProgress(task);

          return (
            <Card
              key={task.id}
              className={cn(
                "p-4 flex items-start gap-3 transition-colors",
                isCompleted && "opacity-70",
                isCancelled && "opacity-50",
                urgency.cardClassName,
              )}
            >
              <Checkbox
                checked={isCompleted}
                disabled={!canEdit || isCancelled}
                onCheckedChange={() => {
                  if (isCompleted) {
                    void onReopen(task.id);
                  } else if (!isCancelled) {
                    requestComplete(task);
                  }
                }}
                className="mt-1"
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span
                    className={cn(
                      "font-medium",
                      isCompleted && "line-through text-muted-foreground",
                    )}
                  >
                    {task.title}
                  </span>
                  <TaskPriorityBadge priority={task.priority} />
                  {subProgress.total > 0 && (
                    <Badge
                      variant="secondary"
                      className={cn(
                        "font-normal gap-1",
                        subProgress.done === subProgress.total &&
                          "bg-emerald-500/15 text-emerald-600 hover:bg-emerald-500/15",
                      )}
                    >
                      <ListTodo className="h-3 w-3" />
                      {subProgress.done}/{subProgress.total}
                    </Badge>
                  )}
                  {urgency.label && (
                    <Badge
                      variant={urgency.badgeVariant ?? "outline"}
                      className={urgency.badgeClassName}
                    >
                      {urgency.label}
                    </Badge>
                  )}
                  {isCompleted && task.outcome === "positive" && (
                    <Badge className="bg-emerald-500/15 text-emerald-600 hover:bg-emerald-500/15">
                      <ThumbsUp className="h-3 w-3 mr-1" /> Positivo
                    </Badge>
                  )}
                  {isCompleted && task.outcome === "negative" && (
                    <Badge className="bg-rose-500/15 text-rose-600 hover:bg-rose-500/15">
                      <ThumbsDown className="h-3 w-3 mr-1" /> Negativo
                    </Badge>
                  )}
                  {isCancelled && <Badge variant="outline">Cancelada</Badge>}
                </div>
                {task.description && (
                  <p className="text-sm text-muted-foreground mt-1 whitespace-pre-wrap">
                    {task.description}
                  </p>
                )}
                {taskTags.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {taskTags.map((tag) => (
                      <Badge key={tag} variant="secondary" className="font-normal text-xs">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                )}
                <div className="flex items-center gap-3 text-xs text-muted-foreground mt-2 flex-wrap">
                  <span className="flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    {task.due_date
                      ? format(parseISO(task.due_date), "dd 'de' MMM", { locale: ptBR })
                      : "Sem data definida"}
                    {task.due_at &&
                      ` · ${format(parseISO(task.due_at), "HH:mm", { locale: ptBR })}`}
                  </span>
                  {isAdmin && task.assignee?.display_name && (
                    <span>Responsável: {task.assignee.display_name}</span>
                  )}
                  {task.creator?.display_name && (
                    <span className="flex items-center gap-1">
                      <User className="h-3 w-3" />
                      Criada por {task.creator.display_name}
                    </span>
                  )}
                  {task.lead?.name && (
                    <span className="truncate max-w-[10rem]">Lead: {task.lead.name}</span>
                  )}
                </div>
              </div>
              {canEdit && !isCancelled && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-8 w-8">
                      <MoreVertical className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => setDetailTarget(task)}>
                      <Eye className="h-4 w-4 mr-2" />
                      Abrir
                    </DropdownMenuItem>
                    {onDuplicate && (
                      <DropdownMenuItem onClick={() => onDuplicate(task)}>
                        <Copy className="h-4 w-4 mr-2" />
                        Duplicar
                      </DropdownMenuItem>
                    )}
                    {!task.project_id &&
                      task.parent_task_id == null &&
                      projects &&
                      projects.length > 0 && (
                        <DropdownMenuItem onClick={() => setLinkTarget(task)}>
                          <FolderKanban className="h-4 w-4 mr-2" />
                          Vincular a projeto...
                        </DropdownMenuItem>
                      )}
                    {!isCompleted && (
                      <DropdownMenuItem onClick={() => requestComplete(task)}>
                        <CheckCircle2 className="h-4 w-4 mr-2" />
                        Concluir
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem onClick={() => setEditDateTarget(task)}>
                      <Calendar className="h-4 w-4 mr-2" />
                      Alterar data
                    </DropdownMenuItem>
                    {isCompleted && (
                      <DropdownMenuItem onClick={() => onReopen(task.id)}>
                        Reabrir
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem
                      onClick={() => setCancelTarget(task)}
                      className="text-destructive focus:text-destructive"
                    >
                      {cancelModeFor(task) === "request" ? (
                        <>
                          <Trash2 className="h-4 w-4 mr-2" />
                          Solicitar exclusão
                        </>
                      ) : (
                        <>
                          <XCircle className="h-4 w-4 mr-2" />
                          Cancelar
                        </>
                      )}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </Card>
          );
        })}
      </div>

      <TaskCompleteDialog
        open={completeTarget !== null}
        taskTitle={completeTarget?.title ?? ""}
        onCancel={() => setCompleteTarget(null)}
        onConfirm={async (outcome) => {
          if (!completeTarget) return;
          await onComplete(completeTarget.id, outcome);
          setCompleteTarget(null);
        }}
      />

      <TaskEditDateDialog
        open={editDateTarget !== null}
        task={editDateTarget}
        onOpenChange={(open) => {
          if (!open) setEditDateTarget(null);
        }}
        onSave={onUpdateDueDate}
      />

      {currentMemberId && (
        <TaskDetailSheet
          open={detailTarget !== null}
          onOpenChange={(open) => {
            if (!open) setDetailTarget(null);
          }}
          task={detailTask}
          workspaceId={workspaceId}
          memberId={currentMemberId}
          isAdmin={isAdmin}
          onSave={onUpdateTask}
          onComplete={requestComplete}
        />
      )}

      <TaskCancelDialog
        open={cancelTarget !== null}
        mode={cancelTarget ? cancelModeFor(cancelTarget) : "cancel"}
        taskTitle={cancelTarget?.title ?? ""}
        hasSubtasks={cancelTarget ? getSubtaskProgress(cancelTarget).total > 0 : false}
        onOpenChange={(open) => {
          if (!open) setCancelTarget(null);
        }}
        onConfirm={handleConfirmCancel}
      />

      {projects && projects.length > 0 && (
        <LinkTaskToProjectDialog
          open={linkTarget !== null}
          onOpenChange={(open) => {
            if (!open) setLinkTarget(null);
          }}
          task={linkTarget}
          projects={projects}
          onSave={onUpdateTask}
        />
      )}
    </>
  );
}
